import { useCallback, useEffect, useRef, useState } from "react";
import { getDealTasks } from "../services/dealTasksApi";
import type { DealTask } from "../types/dealTask";

interface UseDealTasksResult {
  tasks: DealTask[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function sortTasks(tasks: DealTask[]): DealTask[] {
  return [...tasks].sort((a, b) => {
    const statusOrder =
      (a.status === "completed" ? 1 : 0) - (b.status === "completed" ? 1 : 0);
    if (statusOrder !== 0) return statusOrder;

    const dateA = new Date(a.created_at ?? 0).getTime();
    const dateB = new Date(b.created_at ?? 0).getTime();
    return dateB - dateA;
  });
}

export function useDealTasks(dealId?: number): UseDealTasksResult {
  const [tasks, setTasks] = useState<DealTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the latest request for the current dealId may update state; older responses
  // (e.g. for a previous dealId, or from a stale refetch closure) are ignored.
  const latestRequestIdRef = useRef(0);
  const dealIdRef = useRef(dealId);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    // A refetch captured before dealId changed must not invalidate the current request.
    if (dealIdRef.current !== dealId) return;
    const requestId = ++latestRequestIdRef.current;
    const isStale = () =>
      requestId !== latestRequestIdRef.current || dealIdRef.current !== dealId;
    setLoading(true);
    setError(null);
    try {
      const response = await getDealTasks(dealId);
      if (isStale()) return;
      if (response?.success && response.data) {
        const visible = response.data.filter((t) => !t.is_deleted);
        setTasks(sortTasks(visible));
      } else {
        setError(
          typeof response?.message === "string"
            ? response.message
            : "Failed to load tasks",
        );
      }
    } catch (err) {
      if (isStale()) return;
      setError(err instanceof Error ? err.message : "Failed to load tasks");
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    dealIdRef.current = dealId;
    void refetch();
    return () => {
      // Invalidate any in-flight request for the previous dealId (or on unmount).
      latestRequestIdRef.current += 1;
      setLoading(false);
    };
  }, [dealId, refetch]);

  return { tasks, loading, error, refetch };
}
